import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  Briefcase, 
  Target, 
  TrendingUp,
  Award,
  Globe,
  Zap,
  Shield,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: Users,
      title: 'Talent Acquisition',
      description: 'Find the best tech talent for your organization with our comprehensive recruitment solutions.',
      features: [
        'Executive Search',
        'Technical Screening',
        // 'Comprehensive screening processes',
        'Culture Fit Assessment',
        'Onboarding Support'
      ],
      href: '/talent-acquisition',
      color: 'blue'
    },
    {
      icon: Briefcase,
      title: 'HR Consulting',
      description: 'Strategic human resources consulting to optimize your talent acquisition and retention strategies.',
      features: [
        'Talent Strategy',
        'Compensation Analysis',
        'Performance Management',
        // 'Performance management systems',
        'Employee Engagement'
      ],
      href: '/hr-consulting',
      color: 'green'
    },
    {
      icon: Target,
      title: 'Contract Staffing',
      description: 'Flexible staffing solutions for project-based needs and temporary workforce requirements.',
      features: [
        'Short-term Contracts',
        'Project Teams',
        // 'Contract-to-hire options',
        'Specialized skill',
        'Scalable Workforce'
      ],
      href: '/contract-staffing',
      color: 'purple'
    },
    {
      icon: TrendingUp,
      title: 'Tech Training',
      description: 'Upskill your team with cutting-edge technical training programs delivered by industry experts.',
      features: [
        'Custom Curricula',
        'Hands-on Workshops',
        // 'Leadership training',
        'Certification programs',
        'Skill Assessments'
      ],
      href: '/tech-training',
      color: 'orange'
    },
    {
      icon: Award,
      title: 'Employer Branding',
      description: 'Build a strong employer brand that attracts top talent and showcases your company culture.',
      features: [
        'Brand Positioning',
        'Recruitment Marketing',
        // 'Employee value proposition',
        'Candidate Experience',
        'Employer Value Proposition'
      ],
      href: '/employer-branding',
      color: 'pink'
    },
    {
      icon: Globe,
      title: 'Job Matching',
      description: 'Connecting skilled professionals with opportunities that match their expertise and career goals.',
      features: [
        'AI-Powered Matching',
        'Skills Assessment',
        'Career Coaching',
        'Interview Preparation',
        // 'Predictive analytics'
      ],
      href: '/job-matching',
      color: 'indigo'
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Fast Implementation',
      description: 'Quick setup and deployment of our solutions to get you results faster.'
    },
    {
      icon: Shield,
      title: 'Secure & Compliant',
      description: 'Enterprise-grade security and full compliance with industry standards.'
    },
    {
      icon: Users,
      title: 'Expert Support',
      description: 'Dedicated support team available 24/7 to help you succeed.'
    },
    {
      icon: TrendingUp,
      title: 'Scalable Solutions',
      description: 'Solutions that grow with your business and adapt to changing needs.'
    }
  ];

  const stats = [
    { number: '15+', label: 'Years Experience' },
    { number: '500+', label: 'Projects Delivered' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '50+', label: 'Tech Partners' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white',
      green: 'bg-green-100 text-green-600 hover:bg-green-600 hover:text-white',
      purple: 'bg-purple-100 text-purple-600 hover:bg-purple-600 hover:text-white',
      orange: 'bg-orange-100 text-orange-600 hover:bg-orange-600 hover:text-white',
      pink: 'bg-pink-100 text-pink-600 hover:bg-pink-600 hover:text-white',
      indigo: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-600 hover:text-white'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#1A193B] pt-20 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Talent Solutions
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              {/* Comprehensive HR and talent solutions designed to transform your business and drive success. */}
Connecting the right talent with the right opportunities_
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {/* From talent acquisition to HR consulting, we provide end-to-end solutions for modern businesses. */}
              Comprehensive talent solutions designed to meet your specific needs.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group"
              >
                <Link to={service.href} className="block">
                  <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group-hover:border-blue-200 h-full">
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300 ${getColorClasses(service.color)}`}>
                      <service.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2 mb-6">
                      {service.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700 transition-colors duration-200">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose TechMind?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver results through innovation, expertise, and unwavering commitment to client success.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures successful outcomes for every project.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description: 'We start by understanding your unique needs, challenges, and objectives.'
              },
              {
                step: '02',
                title: 'Strategy',
                description: 'Develop a customized solution strategy tailored to your business goals.'
              },
              {
                step: '03',
                title: 'Implementation',
                description: 'Execute the solution with precision, ensuring smooth integration.'
              },
              {
                step: '04',
                title: 'Optimization',
                description: 'Continuously monitor and optimize for maximum performance and results.'
              }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{process.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {process.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A193B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Find Your Perfect Match?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Whether you're looking for exceptional talent or your next career opportunity, we're here to help you succeed. Let's start the conversation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 hover:bg-gray-100 rounded-lg font-semibold transition-colors duration-200"
              >
                Get In Touch
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              {/* <Link
                to="/about"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-blue-600 rounded-lg font-semibold transition-colors duration-200"
              >
                Learn More About Us
              </Link> */}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage; 